import React, { useEffect, useState } from 'react';
import { useGlobalization } from '@Translations';
import { Box, Typography } from '@mui/material';
import {
  StylovyzeComposableDialog,
  Button,
  CurrencyField,
  useUnits,
} from '@innovyze/stylovyze';
import { getLengthUnitCostColumnLabel, normalizeLengthUnitCostFromUserInput } from '@Utils/lengthUnitCost';

interface FixedValueCostDialogProps {
  open: boolean;
  onClose: () => void;
  onFixedValueUpdate: (fixedValue: number) => void;
  isLengthBased?: boolean;
}

const FixedValueCostDialog: React.FC<FixedValueCostDialogProps> = ({
  open,
  onClose,
  onFixedValueUpdate,
  isLengthBased = false,
}) => {
  const { t } = useGlobalization();
  const { system, getSystemUnit } = useUnits();
  const [newFixedValue, setNewFixedValue] = useState<number>(0);

  useEffect(() => {
    if (open) setNewFixedValue(0);
  }, [open]);

  const handleSubmit = () => {
    if (!Number.isNaN(newFixedValue) && newFixedValue >= 0) {
      const storedValue = isLengthBased
        ? normalizeLengthUnitCostFromUserInput(
            newFixedValue,
            system,
            getSystemUnit
          )
        : newFixedValue;
      onFixedValueUpdate(storedValue);
      setNewFixedValue(0);
      onClose();
    }
  };

  const handleCancel = () => {
    setNewFixedValue(0);
    onClose();
  };

  return (
    <StylovyzeComposableDialog
      open={open}
      onCancel={handleCancel}
      title={t('Set unit cost for selected groups')}
      width="500px"
      dialogProps={{
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: { xs: '90vw', sm: '500px' },
            width: '100%',
            margin: { xs: 1, sm: 3 },
          },
        },
      }}
      footer={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
          <Button onClick={handleCancel} color="secondary">
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={Number.isNaN(newFixedValue) || newFixedValue < 0}>
            {t('Submit')}
          </Button>
        </Box>
      }>
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {isLengthBased ? getLengthUnitCostColumnLabel(t, system) : t('Cost')}
        </Typography>
        <CurrencyField
          key={String(open)}
          defaultValue="0"
          fullWidth
          min={0}
          unit={isLengthBased ? 'cost/m' : undefined}
          onChange={e => {
            const raw = e.target.value;
            setNewFixedValue(raw === '' ? Number.NaN : Number.parseFloat(raw));
          }}
        />
      </Box>
    </StylovyzeComposableDialog>
  );
};

export default FixedValueCostDialog;
